import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Field, reduxForm, formValueSelector } from 'redux-form'
import { connect } from 'react-redux'
import {
  Form,
  Icon,
  Button,
  PopupTooltip,
  Alert,
  InputFieldReduxFormWrapper,
  CheckboxFieldReduxFormWrapper,
  PasswordFieldReduxFormWrapper,
  MaskedInputFieldReduxFormWrapper,
  Text,
  withApplicationTheme,
  ProductLine,
} from '@adc/ui-core'
import { trackUserDomainEvent } from '@adc/analytics'
import * as graph from '@adc/sdk'
import classNames from 'classnames/bind'
import { USER_KINDS } from '../../constants'
import SearchLocationInterestReduxWrapper from '../searchField'
import UserService from '../../../../shared/services/user'
import validator, {
  validLocationMin,
  validLocationMax,
} from '../../../../util/authFormValidators'
import { InvestorPreferences } from '../../../../util'
import cls from './styles.scss'
const cx = classNames.bind(cls)

const PRACTICALLY_USELESS_TEMPORARY_ERROR_MESSAGE = 'An unknown error occurred.'
export const FORM_NAME = 'auth_signup'
const LOOKING_TO_BUY_QUESTION =
  InvestorPreferences.IP_QUESTION_CODE.INV_ONBOARDING_LOOKING_TO_BUY
const validate = validator()[FORM_NAME]
const selector = formValueSelector(FORM_NAME)
const ADC_TERMS_LINK = 'https://www.auction.com/lp/legal/terms/'
const ADC_PRIVACY_LINK = 'https://www.auction.com/lp/legal/privacy/'
const INVALID_US_PHONE_MESSAGE =
  'Must be a valid US phone number in 2134567890 or 213-456-7890 or (213)-456-7890 format or 1-213-456-7890, or international phone with format +455 12345678'
const PHONE_INPUT_FIELD_MASK = [
  '(',
  /[1-9]/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
]

const DEFAULT_PROPOSITIONS = [
  {
    label: 'More Properties',
  },
  {
    label: 'More Tools',
  },
  {
    label: 'More Inventory',
  },
]

const ABNTest = props => {
  const { test: ABNTest, children } = props
  const version = ABNTest?.test?.version || 'control'
  return children.find(child => child.props.value === version)
}

class SignUpForm extends Component {
  static propTypes = {
    elmId: PropTypes.string,
    cls: PropTypes.object,
    title: PropTypes.string,
    subTitle: PropTypes.string,
    cta: PropTypes.string,
    onFail: PropTypes.func,
    onSuccess: PropTypes.func,
    signupEndPoint: PropTypes.string,
    submitting: PropTypes.bool.isRequired,
    updateSavedSearches: PropTypes.func,
    handleSubmit: PropTypes.func.isRequired,
    saveSearch: PropTypes.bool,
    shouldRequirePhone: PropTypes.bool,
    userKind: PropTypes.oneOf(Object.values(USER_KINDS)),
    abnTest: PropTypes.object,
    initialValues: PropTypes.object,
    disableEmail: PropTypes.bool,
    theme: PropTypes.object,
    switchToLogin: PropTypes.func,
    email: PropTypes.string,
    showPhoneInputHelpText: PropTypes.bool,
    invitationToken: PropTypes.string,
    notificationSettingsRedesignEnabled: PropTypes.bool,
  }

  static defaultProps = {
    elmId: 'auth_toggle_signup',
    cls: {},
    onFail: _.noop,
    onSuccess: _.noop,
    updateSavedSearches: _.noop,
    title: 'Sign Up & Get More',
    subTitle: '',
    cta: 'Create Your Account',
    signupEndPoint: '/apps/api/signup',
    saveSearch: false,
    shouldRequirePhone: false,
    userKind: USER_KINDS.CONSUMER,
    abnTest: {},
    theme: {},
    switchToLogin: null,
    email: '', // from redux state
    showPhoneInputHelpText: false,
    invitationToken: null,
    notificationSettingsRedesignEnabled: false,
  }

  state = {
    errorCode: null,
    error: null,
    key: 'value',
    popupState: false,
  }

  setPopupStateOpen = () => {
    this.setState({ popupState: true })
  }

  setPopupStateClose = () => {
    this.setState({ popupState: false })
  }

  submit = async values => {
    const {
      onFail: onFailCb,
      onSuccess: onSuccessCb,
      saveSearch,
      signupEndPoint,
      userKind,
      theme,
      invitationToken,
    } = this.props
    const { firstName, lastName, email, password, phone, emailOptIn } = values
    const trackEvent = (data, user = {}) => {
      trackUserDomainEvent({
        domainEventTypeName:
          graph.enums.DomainEventTypeName.UserSignupCompleted,
        user,
        data,
      })
    }
    const onSuccess = user => {
      trackEvent({ email }, user)
      return onSuccessCb(user)
    }
    const onFail = error => {
      trackEvent({ email, error })
      return onFailCb(error)
    }

    try {
      let payload = {
        firstName: _.trim(firstName),
        lastName: _.trim(lastName),
        email: _.trim(email),
        password: _.trim(password),
        phone,
        ...(invitationToken && { invitationToken }),
      }

      if (theme.productLine === ProductLine.SmartSale) {
        payload = {
          ...payload,
          updateSubscribe: !!emailOptIn,
          accountCreationIntent: graph.enums.AccountCreationIntent.Selling,
        }
      } else if (userKind === USER_KINDS.CONSUMER) {
        // Build the seed searches in-memory only (populates the wizard's
        // savedSearches state via updateSavedSearches, which drives the
        // post-signup SRP redirect). We intentionally do NOT persist them:
        // the generated saved search is no longer sent in the signup payload.
        if (saveSearch) this.generateSavedSearches(values)
        payload = {
          ...payload,
          updateSubscribe: !!emailOptIn,
          investorPreferences: this.generateInvestorPreferences(values),
        }
      }

      const authResponse = await UserService.create(signupEndPoint, payload)
      if (!authResponse.body.login) throw authResponse
      this.setState({ error: null })
      // Fall back to the submitted email so downstream tracking can hash it.
      const user = { email, ..._.get(authResponse, 'body.user') }

      try {
        // bidder signature contains the info about the number of registrations
        const bidderSignatureResponse = await UserService.fetchBidderSignature()

        const userWithBidderSignature =
          bidderSignatureResponse.body.data?.current_user || {}

        if (userWithBidderSignature.bidder_signature?.length > 0) {
          return onSuccess({ ...user, ...userWithBidderSignature })
        }
      } catch (err) {
        // TODO: should we do anything here ?
      }

      return onSuccess(user)
    } catch (err) {
      console.error(err)
      this.setState({ error: err })
      const authError = { ...err.body, authType: 'signup' }
      return onFail(authError)
    }
  }

  generateInvestorPreferences = values => {
    const { locationInterest } = values
    const questionSets = InvestorPreferences.IP_OBJECT()
    const parsedQuestionSets = []
    const locationInterestValue = {
      [LOOKING_TO_BUY_QUESTION]: locationInterest,
    }
    const locationInterestSet = InvestorPreferences.formToStore(
      questionSets[LOOKING_TO_BUY_QUESTION],
      locationInterestValue
    )
    parsedQuestionSets.push(InvestorPreferences.apiValue(locationInterestSet))
    return parsedQuestionSets
  }

  generateSavedSearches = ({ locationInterest }) => {
    const body = locationInterest.map(place => ({
      data_source_code: 'ADC',
      email_alert_type: 'DAILY',
      subscribed_to_notifications: true,
      param_version: 1,
      parameters: {
        ...InvestorPreferences.placeToPreference(place),
        listing_type: 'active',
        sort: 'resi_sort_v2',
        nearby_search: 'y',
        offset: 0,
        limit: 48,
        auction_type: 'residential',
        use_resi_collection: 'y',
        show_sold_assets: 'y',
      },
      saved_search_name: InvestorPreferences.placeToLabel(place),
    }))
    this.props.updateSavedSearches(body)

    return body
  }

  getErrorMessage = () => {
    const { error } = this.state
    const { email, switchToLogin } = this.props
    if (!error) return null
    if (!error.body) return PRACTICALLY_USELESS_TEMPORARY_ERROR_MESSAGE

    const firstError = error.body.errors?.[0]

    const errorCode = firstError?.extensions?.specificErrorCode
    switch (errorCode) {
      case 'ACCOUNT_ALREADY_EXISTS':
      case 'CONFIRMATION_PENDING':
        return (
          <span>
            This email is already registered.
            {switchToLogin && (
              <>
                <br />
                Click{' '}
                <a className={cx('link')} onClick={() => switchToLogin(email)}>
                  here
                </a>{' '}
                to login.
              </>
            )}
          </span>
        )
      default:
        // hack until BE can give us a legit error code
        if (firstError?.message.includes('Must be a valid US phone number')) {
          return INVALID_US_PHONE_MESSAGE
        }
        return PRACTICALLY_USELESS_TEMPORARY_ERROR_MESSAGE
    }
  }

  renderConsumerSubTitle = (elmId, testVariant) => {
    return (
      <ABNTest test={testVariant}>
        <div value='control'>{this.controlConsumerSubTitle(elmId)}</div>
        <div value='simple'>
          {this.propositionSubTitle(elmId, testVariant.propositions)}
        </div>
        <div value='smarter'>
          {this.propositionSubTitle(elmId, testVariant.propositions)}
        </div>
        <div value='proposition-text'>
          {this.propositionSubTitleVariant(elmId, testVariant.propositions)}
        </div>
      </ABNTest>
    )
  }

  controlConsumerSubTitle = elmId => (
    <div className={cx('sub-title')}>
      <div
        className={cx('sub-title-section')}
        data-elm-id={`${elmId}_title_exclusive_deals`}
      >
        <div className={cx('icon-container')}>
          <Icon
            name='house-circle-check'
            iconStyle='light'
            color='adcLightGreen60'
            className={cx('icon')}
          />
        </div>
        <label>
          <span>MORE</span> properties not on the MLS and priced to sell
        </label>
      </div>
      <div
        className={cx('sub-title-section')}
        data-elm-id={`${elmId}_title_reach_your_goals`}
      >
        <div className={cx('icon-container')}>
          <Icon
            name='lightbulb-gear'
            iconStyle='light'
            color='adcLightBlue'
            className={cx('icon')}
          />
        </div>
        <label>
          <span>MORE</span> tools and investor support
        </label>
      </div>
      <div
        className={cx('sub-title-section')}
        data-elm-id={`${elmId}_title_bid_from_anywhere`}
      >
        <div className={cx('icon-container')}>
          <Icon
            name='house-laptop'
            iconStyle='light'
            color='adcDarkGreen'
            className={cx('icon')}
          />
        </div>
        <label>
          <span>MORE</span> online inventory from coast to coast
        </label>
      </div>
    </div>
  )

  propositionSubTitle = (elmId, propositions = []) => (
    <div className={cx('sub-title')}>
      {propositions.map((proposition, i) => (
        <div
          key={i}
          className={cx('sub-title-proposition-section')}
          data-elm-id={`${elmId}_${proposition.label
            .toLowerCase()
            .replace(/ /g, '_')}`}
        >
          <div className={cx('proposition-container')}>
            <label>{proposition.label}</label>
          </div>
        </div>
      ))}
    </div>
  )

  propositionSubTitleVariant = (elmId, propositions = []) => (
    <div className={cx('sub-title-text')}>
      {propositions.map(
        (proposition, i) =>
          proposition.label && (
            <Text
              key={i}
              size='massive'
              weight='heavy'
              as='h2'
              className={cx('sub-title-proposition-text')}
              data-elm-id={`${elmId}_${proposition.label
                .toLowerCase()
                .replace(/ /g, '_')}`}
            >
              {proposition.label}
            </Text>
          )
      )}
      <Text as='h3'>Sign Up Now</Text>
    </div>
  )

  renderTermsAndPrivacyStatement = () => {
    const { elmId, cta, userKind, notificationSettingsRedesignEnabled } =
      this.props
    const isConsumer = userKind === USER_KINDS.CONSUMER
    const showEmailOptIn = isConsumer && !notificationSettingsRedesignEnabled
    const showMarketingCopy = isConsumer && notificationSettingsRedesignEnabled

    const interestTooltip = (
      <PopupTooltip
        trigger={
          <a
            data-elm-id={`${elmId}_learn_more`}
            tabIndex='0'
            aria-label='sign up learn more'
          >
            Learn more
          </a>
        }
        hideOnScroll
        onOpen={this.setPopupStateOpen}
        open={this.state.popupState}
        position='top right'
        on='click'
        inverted
      >
        <div className={cx('tooltip-close-icon')}>
          <Icon
            name='times'
            iconStyle='light'
            color='white'
            onClick={this.setPopupStateClose}
          />
        </div>
        <span>
          Auction.com may use the information provided by you to contact you by
          email, telephone or postal mail for marketing purposes in accordance
          with our&nbsp;
          <a
            className={cx('inline-link')}
            href={ADC_PRIVACY_LINK}
            target='_blank'
            rel='noreferrer'
          >
            Privacy Statement
          </a>
          . You can change your communication preferences in your Profile.
        </span>
      </PopupTooltip>
    )

    const termsLink = (
      <a
        data-elm-id={`${elmId}_legal`}
        href={ADC_TERMS_LINK}
        target='_blank'
        rel='noreferrer'
      >
        Terms of Use
      </a>
    )
    const privacyLink = (
      <a
        data-elm-id={`${elmId}_privacy_statement`}
        href={ADC_PRIVACY_LINK}
        target='_blank'
        rel='noreferrer'
      >
        Privacy Statement
      </a>
    )

    if (showMarketingCopy) {
      return (
        <div className={cx('form-field', 'form-label', 'form-options-root')}>
          <div
            className={cx('form-option')}
            data-elm-id={`${elmId}_email_marketing_copy`}
          >
            By creating an account, you agree to Auction.com's {termsLink} and{' '}
            {privacyLink}, and consent to receive property recommendations and
            other marketing emails from Auction.com. You may opt out of
            marketing emails at any time.
          </div>
        </div>
      )
    }

    return (
      <div className={cx('form-field', 'form-label', 'form-options-root')}>
        {showEmailOptIn && (
          <div className={cx('form-option', 'flex')}>
            <Field
              type='checkbox'
              name='emailOptIn'
              elmId={`${elmId}_email_opt_in`}
              aria-label='email opt in checkbox'
              label={
                <span className={cx('checkbox-label')}>
                  Get properties or listings of interest.
                </span>
              }
              component={CheckboxFieldReduxFormWrapper}
            />{' '}
            {interestTooltip}
          </div>
        )}
        <div
          className={cx('form-option', {
            'indent-terms': showEmailOptIn,
          })}
        >
          By clicking "{cta}" you agree to Auction.com's {termsLink}
          &nbsp; and &nbsp;{privacyLink}.
        </div>
      </div>
    )
  }

  render() {
    const {
      cls: extcls,
      elmId,
      handleSubmit,
      shouldRequirePhone,
      submitting,
      title,
      subTitle,
      cta,
      userKind,
      abnTest,
      initialValues,
      disableEmail,
      theme,
      showPhoneInputHelpText, // this would be passed in as true if we get the is_agent_user query param in URL
    } = this.props

    const isSmartSale = theme.productLine === ProductLine.SmartSale

    const errorMessage = this.getErrorMessage()
    let phonePlaceholder = 'Business Phone'
    if (!shouldRequirePhone) {
      phonePlaceholder += ' (optional)'
    }

    const displayTitleHeader = abnTest?.test?.version !== 'proposition-text'
    const userIsOfSmartSaleAndPrivateSeller =
      userKind === USER_KINDS.PRIVATE_SELLER &&
      isSmartSale &&
      showPhoneInputHelpText

    return (
      <div className={cx('root', extcls.root)} data-elm-id={elmId}>
        {isSmartSale ? (
          <div className={cx('header')}>
            <h2 className={cx('title')}>Sign Up Now</h2>
            <Text>
              Create your free account. Browse properties, use smarter tools,
              and connect with buyers and sellers.
            </Text>
          </div>
        ) : (
          <div className={cx('header')}>
            {!_.isEmpty(abnTest) && displayTitleHeader && title && (
              <h2 className={cx('title')}>{title}</h2>
            )}
            {!_.isEmpty(abnTest) &&
              userKind === USER_KINDS.CONSUMER &&
              this.renderConsumerSubTitle(elmId, abnTest)}
            {!_.isEmpty(abnTest) && subTitle && <p>{subTitle}</p>}
            <>{this.propositionSubTitleVariant(elmId, DEFAULT_PROPOSITIONS)}</>
          </div>
        )}

        <Alert
          elmId={`${elmId}_error_message`}
          className={cls['form-error']}
          role='error'
          showIcon
          content={errorMessage}
          hidden={!errorMessage || submitting}
          dismissible={false}
        />

        <Form
          className={cx('u-pt-1')}
          onSubmit={handleSubmit(this.submit)}
          data-elm-id={`${elmId}_form`}
        >
          <Field
            autoFocus
            type='text'
            name='firstName'
            placeholder='First Name'
            elmId={`${elmId}_first_name`}
            aria-label='sign up first name'
            component={InputFieldReduxFormWrapper}
            className={cx('form-field')}
            autoComplete='off'
            hideLabel
          />

          <Field
            type='text'
            name='lastName'
            placeholder='Last Name'
            elmId={`${elmId}_last_name`}
            aria-label='sign up last name'
            component={InputFieldReduxFormWrapper}
            className={cx('form-field')}
            autoComplete='off'
            hideLabel
          />

          <Field
            type='email'
            name='email'
            placeholder='Email'
            elmId={`${elmId}_email`}
            aria-label='sign up email'
            component={InputFieldReduxFormWrapper}
            className={cx('form-field')}
            autoComplete='email'
            hideLabel
            disabled={initialValues?.email && disableEmail}
          />

          <Field
            type='password'
            name='password'
            placeholder='Password'
            elmId={`${elmId}_password`}
            aria-label='sign up password'
            component={PasswordFieldReduxFormWrapper}
            className={cx('form-field')}
            autoComplete='new-password'
            hideLabel
          />

          {(userKind === USER_KINDS.CONSUMER ||
            userKind === USER_KINDS.PRIVATE_SELLER) && (
            <>
              <Field
                name='phone'
                placeholder={phonePlaceholder}
                elmId={`${elmId}_phone`}
                aria-label='sign up business phone'
                component={MaskedInputFieldReduxFormWrapper}
                className={cx('form-field')}
                mask={PHONE_INPUT_FIELD_MASK}
                inputLabel='+1'
                guide={false}
                hideLabel
              />
              {userKind === USER_KINDS.CONSUMER && !isSmartSale && (
                <div data-elm-id={`${elmId}_investor_preferences`}>
                  <Field
                    name='locationInterest'
                    className={cx('form-field')}
                    elmId={`${elmId}_location_interest`}
                    validate={[validLocationMin, validLocationMax]}
                    component={SearchLocationInterestReduxWrapper}
                  />
                </div>
              )}
              {userIsOfSmartSaleAndPrivateSeller && (
                <div className={cx('form-field', 'form-label', 'form-option')}>
                  By providing your phone number, you agree to receive text
                  messages related to your transactions and account activity.
                  Message and data rates may apply. Reply STOP to opt out at any
                  time.
                </div>
              )}
            </>
          )}

          {!userIsOfSmartSaleAndPrivateSeller &&
            this.renderTermsAndPrivacyStatement()}

          <Button
            fluid
            type='submit'
            role='primary'
            elmId={`${elmId}_signup_button`}
            disabled={submitting}
            className={cx('form-field', extcls.cta)}
            loading={submitting}
          >
            {submitting ? 'Creating Your Account...' : cta}
          </Button>

          {userIsOfSmartSaleAndPrivateSeller &&
            this.renderTermsAndPrivacyStatement()}

          {!isSmartSale && (
            <h3 className={cx('text-centered', extcls.footnote)}>
              Browse, Save & Bid with Auction.com
            </h3>
          )}
        </Form>
      </div>
    )
  }
}

const mapStateToProps = state => {
  const email = selector(state, 'email')
  return {
    email,
  }
}

const connectedForm = connect(mapStateToProps)(
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
    validate,
    touchOnBlur: false,
  })(SignUpForm)
)

const themedForm = withApplicationTheme(connectedForm)

themedForm.USER_KINDS = USER_KINDS

export default themedForm
export { SignUpForm } // for test
